import React from "react";
import { Card, Empty, ErrorState, PageHeading, Pill, Rate, RowsSkeleton, Table } from "../ui";
import { BarList, SegmentBar } from "../charts";
import { useFilters } from "../filters";
import { capabilityLabel, useView } from "../data";
import { TASK_LABELS, fmt, statusOf, words } from "../format";

/** Dimensions that mean "asked for, and the agent could not do it" — the roadmap list. */
const UNMET = {
  declined: { label: "Declined as out of scope", help: "Requests the agent turned down." },
  signal: { label: "No search filter can apply it", help: "Criteria users want that the search cannot filter on." },
  enrichment_unmatched: { label: "No enrichment provides it", help: "Fields users wanted filled that no template provides." },
};

/** What people use the agent for — and, most useful for planning, what they asked for that it cannot do yet. */
export default function Demand() {
  const { params, set } = useFilters();
  const { data, loading, error } = useView("demand", params);
  const by = data?.byDimension ?? {};
  const tasks = data?.tasks ?? [];
  const caps = data?.capabilities ?? [];
  const unmet = Object.keys(UNMET).filter((d) => by[d]?.length);
  const asked = Object.keys(by).filter((d) => !UNMET[d] && by[d]?.length);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PageHeading title="Demand">What people use the agent for, which capabilities that starts, and what they asked for that it could not do.</PageHeading>

      <div className="aa-grid aa-grid-2">
        <Card title="Kinds of requests" subtitle="Each reviewed turn, filed under what the user wanted" flush>
          {loading ? (
            <RowsSkeleton rows={5} />
          ) : error ? (
            <ErrorState error={error} />
          ) : tasks.length ? (
            <Table
              defaultSort={{ key: "turns", dir: "desc" }}
              columns={[
                { key: "task", label: "What they wanted", render: (r) => <span style={{ fontWeight: 500 }}>{TASK_LABELS[r.task] ?? words(r.task)}</span> },
                { key: "turns", label: "Turns", align: "right", render: (r) => fmt(r.turns) },
                {
                  key: "split",
                  label: "Outcome",
                  width: 140,
                  sortable: false,
                  render: (r) => (
                    <SegmentBar
                      segments={[
                        { label: "Did it", value: r.success ?? 0, color: "var(--aa-good)" },
                        { label: "Partly", value: r.partial ?? 0, color: "var(--aa-warn)" },
                        { label: "Did not", value: r.failure ?? 0, color: "var(--aa-bad)" },
                      ]}
                    />
                  ),
                },
                {
                  key: "rate",
                  label: "Success",
                  align: "right",
                  sort: (r) => successOf(r),
                  render: (r) => <Rate value={successOf(r)} status={statusOf(successOf(r), { good: 0.85, bad: 0.6 })} />,
                },
              ]}
              rows={tasks.map((t) => ({ ...t, id: t.task }))}
            />
          ) : (
            <Empty compact title="No reviewed turns yet">
              Each turn is filed under a kind of request once the reviewer reads it.
            </Empty>
          )}
        </Card>
        <Card title="Capabilities started" subtitle="Click one for its stages">
          {caps.length ? (
            <BarList
              limit={8}
              valueLabel="Steps"
              rows={caps.map((r) => ({ key: r.capability, label: capabilityLabel(r.capability), value: r.started, onClick: () => set({ tab: "capabilities" }) }))}
            />
          ) : (
            <Empty compact icon="capabilities" title="No capability ran in this period" />
          )}
        </Card>
      </div>

      <Card title="What the agent could not do" subtitle="The roadmap list: asked for, and not possible yet" actions={unmet.length ? <Pill tone="bad">{fmt(unmet.reduce((s, d) => s + by[d].reduce((x, v) => x + v.count, 0), 0))} asks</Pill> : null}>
        {unmet.length ? (
          <div className="aa-grid aa-grid-3">
            {unmet.map((d) => (
              <div key={d}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{UNMET[d].label}</div>
                <div style={{ fontSize: 12, color: "var(--aa-muted)", marginBottom: 10 }}>{UNMET[d].help}</div>
                <BarList color="var(--aa-bad)" limit={8} rows={by[d].map((v) => ({ key: v.value, label: v.value, value: v.count }))} />
              </div>
            ))}
          </div>
        ) : (
          <Empty compact icon="checkCircle" title="Nothing unmet in this period">
            The agent declined nothing and applied every criterion it was given.
          </Empty>
        )}
      </Card>

      {asked.length > 0 && (
        <Card title="Most asked-for values" subtitle="The words people used, most frequent first">
          <div className="aa-grid aa-grid-3">
            {asked.map((d, i) => (
              <div key={d}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>{words(d)}</div>
                <BarList color={`var(--aa-s${(i % 5) + 1})`} limit={8} rows={by[d].map((v) => ({ key: v.value, label: v.value, value: v.count }))} />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function successOf(r) {
  const judged = (r.success ?? 0) + (r.partial ?? 0) + (r.failure ?? 0);
  return judged ? (r.success ?? 0) / judged : null;
}
